<template>
  <ValidationObserver ref="observer" slim>
    <b-form class="col-12" :novalidate="true">
      <b-card>
        <b-form-row>
          <h5 class="col-12 text-primary font-weight-bold pb-2">Managed Landowner Information</h5>
        </b-form-row>
        <b-form-row>
          <b-col lg="4">
            <ValidationProvider name="First Name" rules="required" v-slot="{ errors, classes }">
              <b-form-group label-for="firstName">
                <template v-slot:label><span class="req-ind">*</span>First Name</template>
                <b-form-input
                  id="firstName"
                  v-model="user.firstName"
                  placeholder="Enter First Name"
                  maxlength="50"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'firstName')"
                  :disabled="mfsAdmin"
                />
                <InputErrors :errors="errors" errorsIdPrefix="firstName" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col lg="4">
            <b-form-group label="Middle Name or Initial" label-for="middleName">
              <b-form-input
                id="middleName"
                name="middleName"
                v-model="user.middleName"
                placeholder="Enter Middle Name"
                maxlength="50"
                :disabled="mfsAdmin"
              />
            </b-form-group>
          </b-col>
          <b-col lg="4">
            <ValidationProvider name="Last Name" rules="required" v-slot="{ errors, classes }">
              <b-form-group label-for="lastName">
                <template v-slot:label><span class="req-ind">*</span>Last Name</template>
                <b-form-input
                  id="lastName"
                  name="lastName"
                  v-model="user.lastName"
                  placeholder="Enter Last Name"
                  maxlength="50"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'lastName')"
                  :disabled="mfsAdmin"
                />
                <InputErrors :errors="errors" errorsIdPrefix="lastName" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group label="Company Name" label-for="companyName">
              <b-form-input
                id="companyName"
                name="companyName"
                v-model="user.companyName"
                placeholder="Enter Company Name"
                maxlength="100"
                :disabled="mfsAdmin"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col lg="4">
            <ValidationProvider
              name="Phone"
              :rules="{ required: '', phoneRegex: /^[0-9]{10}$/ }"
              v-slot="{ errors, classes }"
            >
              <div role="group" class="form-group">
                <label for="phone">
                  <span class="req-ind">*</span>Phone
                </label>
                <b-form-input
                  id="phone"
                  name="phone"
                  v-model="user.phoneNumber"
                  placeholder="Enter Phone Number"
                  type="tel"
                  maxlength="10"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'phone')"
                  :disabled="mfsAdmin"
                />
                <InputErrors :errors="errors" errorsIdPrefix="phone" />
              </div>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col lg="4">
            <ValidationProvider
              name="Email"
              rules="email"
              v-slot="{ errors, classes }">
              <div role="group" class="form-group">
                <label for="email">
                  Email
                </label>
                <b-form-input
                  type="email"
                  id="email"
                  name="email"
                  v-model="user.email"
                  placeholder="Enter Email"
                  :class="classes"
                  autocomplete="email"
                  :aria-describedby="$errorsIdString(errors, 'email')"
                  :disabled="mfsAdmin"
                />
                <InputErrors :errors="errors" errorsIdPrefix="email" />
              </div>
            </ValidationProvider>
          </b-col>
        </b-form-row>
      </b-card>
      <b-card>
        <AddressForm
          :address="user.physicalAddress"
          addressType="Physical"
          addressSelector="physical"
        />
        <hr>
        <AddressForm
          :disabled="user.mailingSameAsPhysical"
          :address="user.mailingAddress"
          addressType="Mailing"
          addressSelector="mailing"
        >
          <template v-slot:header>
            <b-form-checkbox
              id="mailingSameAsPhysical"
              v-model="user.mailingSameAsPhysical"
              name="mailingSameAsPhysical"
              @input="clearMailingAddress"
              :disabled="mfsAdmin"
            >
              Same as Physical Address
            </b-form-checkbox>
          </template>
        </AddressForm>
      </b-card>
      <b-row v-if="withinModal === false" class="w-100" align-h="center">
        <b-col v-if="edit" cols="3">
          <b-button
            class="col"
            variant="danger"
            pill
            @click="cancel"
            :disabled="submitting"
            aria-label="Cancel"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col md="5" lg="3">
          <b-button
            class="col"
            variant="primary"
            pill
            @click="submitWrapper"
            :disabled="mfsAdmin || submitting"
            aria-label="Submit"
          >
            {{edit ? 'Update Managed Landowner' : 'Create Managed Landowner'}}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { initialAddress } from '@/helpers';
import AddressForm from '@/components/AddressForm.vue';
import FormSubmit from '@/mixins/FormSubmit';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'NewManagedLandownerForm',
  components: {
    AddressForm,
  },
  mixins: [FormSubmit, Utilities],
  props: {
    initialUser: {
      type: Object,
      required: false,
    },
    edit: {
      type: Boolean,
      required: true,
    },
    scrollId: {
      type: String,
      required: false,
      default: 'newManagedLandownerModal',
    },
    withinModal: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    let user = {
      firstName: '',
      middleName: '',
      lastName: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      physicalAddress: initialAddress(),
      mailingAddress: initialAddress(),
      mailingSameAsPhysical: false,
      isAlsoOperator: false,
    };
    user = this.initialUser ? this.mergeTargetProps(user, this.initialUser) : user;
    return {
      user,
    };
  },
  computed: {
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapGetters('User', ['mfsAdmin']),
  },
  methods: {
    async submitWrapper() {
      if (this.mfsAdmin) {
        return;
      }
      const data = { ...this.user };
      if (data.mailingSameAsPhysical) data.mailingAddress = data.physicalAddress;
      const url = 'Notification/createManagedLandowner';
      await this.submit(
        this.$refs.observer,
        url,
        data,
      );
    },
    invalidError(firstEl) {
      const scrollContainer = document.querySelector(`#${this.scrollId}`);
      this.$scrollTo(firstEl, {
        container: scrollContainer,
      });
    },
    clearMailingAddress() {
      if (this.mfsAdmin) {
        return;
      }
      this.user.mailingAddress = initialAddress();
    },
    submitSuccess() {
      this.$emit('submitted');
    },
    submitError() {
      this.$emit('submitFailed');
    },
  },
};
</script>
